import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Lock, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";

export default function AdminLogin() {
  const { user, login, ready } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  if (ready && user && user.role === "admin") {
    return <Navigate to="/admin-dashboard" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/admin-dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-5">
        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0E0E12] p-8"
        >
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#E0007A]/15 border border-[#E0007A]/30 mb-6">
            <Lock className="h-4 w-4 text-[#E0007A]" />
          </div>
          <div className="font-mono-label text-[#00E5FF]">Admin Workplace</div>
          <h1 className="font-display text-3xl md:text-4xl mt-2">Sign in.</h1>
          <p className="text-white/50 mt-2 text-sm">
            Enter your admin credentials to manage the prompt library.
          </p>

          <label className="block mt-8">
            <span className="font-mono-label text-white/60">Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              data-testid="login-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 text-white placeholder-white/30"
              placeholder="you@example.com"
            />
          </label>

          <label className="block mt-4">
            <span className="font-mono-label text-white/60">Password</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              data-testid="login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 text-white placeholder-white/30"
              placeholder="••••••••"
            />
          </label>

          {error && (
            <div
              data-testid="login-error"
              className="mt-4 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit-btn"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#E0007A] hover:bg-[#ff1493] disabled:opacity-60 text-white font-semibold py-3 glow-magenta transition-transform duration-200 hover:scale-[1.02]"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {busy ? "Signing in…" : "Sign in"}
          </button>

        </form>
      </div>
    </div>
  );
}
